import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { gunzipSync } from 'fflate';
import { ZstdCodec } from 'zstd-codec';


@Injectable({
  providedIn: 'root'
})
export class CryptoDecryptService {
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();

  simpleZstd: any;

  constructor() {
    ZstdCodec.run(zstd => {
      this.simpleZstd = new zstd.Simple();
    });
  }

  private encode(str: string): Uint8Array {
    return this.encoder.encode(str);
  }

  private decode(data: BufferSource): string {
    return this.decoder.decode(data);
  }

  private async deriveKeyFromSecret(secret: Uint8Array, jti: Uint8Array): Promise<CryptoKey> {
    // Concatena os dois arrays em um só
    const keyMaterialBytes = new Uint8Array(secret.length + jti.length);
    keyMaterialBytes.set(secret, 0);
    keyMaterialBytes.set(jti, secret.length);

    // Importa a chave "raw" para usar com HKDF
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      keyMaterialBytes,
      { name: 'HKDF' },
      false,
      ['deriveKey']
    );

    // Deriva a chave final AES-256-GCM
    return crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(32).fill(0), // sal fixo (pode trocar por algo seguro/único)
        info: new Uint8Array([])          // info opcional
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );
  }

  /**
   * Descriptografa o body de um response encriptado no formato Mar Saúde (cipher suite 1).
   * O IV é os 12 primeiros bytes do body, seguido pelo ciphertext.
   */
  decryptBody(encrypted: ArrayBuffer, token: string, encoding?: 'rbe1c1' | 'rbe1c2', presharedKey: string = environment.presharedKey): Observable<any> {
    return from(this.decryptPromise(encrypted, token, encoding, presharedKey));
  }

  base64ToBytes(base64) {
    const binaryString = atob(base64); // decode Base64 to binary string
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Função auxiliar para manter a lógica async/await isolada
   */
  private async decryptPromise(encrypted: ArrayBuffer, token: string, encoding: 'rbe1c1' | 'rbe1c2', presharedKey: string): Promise<any> {
    const data = new Uint8Array(encrypted);

    if (data.byteLength < 13) {
      throw new Error('Payload inválido: tamanho insuficiente para IV + ciphertext');
    }

    // 2) Formato RBE v1: IV (12 bytes) + ciphertext
    const iv = data.slice(0, 12);
    const ciphertext = data.slice(12);

    // 3) Extrai jti do JWT
    const jti = token ? (JSON.parse(this.base64UrlDecode(token.split('.')[1])).jti as string) : '';


    // 4) Deriva chave e descriptografa AES-GCM
    const secret: Uint8Array = this.base64ToBytes(presharedKey);
    const key = await this.deriveKeyFromSecret(secret, this.encode(jti));

    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);

    const raw = await this.maybeDecompress(decrypted, encoding);
    return raw ? JSON.parse(this.decode(raw)) : [];
  }

  private async maybeDecompress(buf: ArrayBuffer, encoding: 'rbe1c1' | 'rbe1c2'): Promise<ArrayBuffer> {
    const isGzip = encoding === 'rbe1c1';
    const isZstd = encoding === 'rbe1c2';
    if (isGzip) return this.decompressGzip(buf);
    if (isZstd) return this.decompressZstd(buf);
    return buf;
  }


  private async decompressZstd(compressed: ArrayBuffer): Promise<ArrayBuffer> {
    const data = new Uint8Array(compressed);
    return new Promise(resolve => {
      ZstdCodec.run(zstd => {
        const streaming = new zstd.Streaming();
        const result = streaming.decompress(data);
        resolve(result);
      });
    });
  }

  private async decompressGzip(compressed: ArrayBuffer): Promise<any> {
    if (typeof DecompressionStream !== 'undefined') {
      const ds = new DecompressionStream('gzip');
      const res = new Response(new Blob([compressed]).stream().pipeThrough(ds));
      return await res.arrayBuffer();
    }
    return gunzipSync(new Uint8Array(compressed)).buffer;
  }

  /** Decodifica base64url (JWT payload) para string. Usa Buffer no SSR quando necessário. */
  private base64UrlDecode(b64url: string): string {
    // converte base64url -> base64
    let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    // padding
    while (b64.length % 4) b64 += '=';
    if (typeof atob === 'function') {
      return decodeURIComponent(escape(atob(b64)));
    }
    // SSR/Node
    // @ts-ignore
    return Buffer.from(b64, 'base64').toString('utf8');
  }


}
